import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ToastController } from "ionic-angular";
import { AuthService } from "../../../app/auth/auth.service";
import { QaQueueService } from "../../../providers/qa-queue/qa-queue.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import UserType from "../models/user-type";

@Component({
  selector: "users-grid",
  templateUrl: "users-grid.html",
})
export class UsersGridComponent implements OnInit, OnDestroy {
  /** CO-3507: Company:QAControls:Users:EDIT, resolved by the host page. Default false
   *  so the grid is read-only until IAM says otherwise (fail-closed). */
  @Input() canEdit: boolean = false;

  query: string = "";
  users: UserType[] = [];
  usersPayload: UserType[] = [];
  usersForm: FormGroup;
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  hasValidInput: boolean = true;
  totalPageCount: number = 0;
  pageSize: number = 25;
  currentPage: number = 0;
  tenantId: number;
  sortOptions = {
    column: "userName",
    ascending: true,
  };

  // CO-3507: the searchbar magnifier. visit-types-grid wired this from the start; this
  // grid did not, so the same icon searched in one tab and silently did nothing in the
  // other. Ionic-3 renders the icon inside <ion-searchbar>, so it is reachable only via
  // the host element rather than a template binding.
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private toastCtrl: ToastController,
    private authSvc: AuthService,
    private qaQueueService: QaQueueService,
    private utilityService: UtilityService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.usersForm = this.formBuilder.group({});
    this.tenantId = this.authSvc.getUser().tenantId;
    this.firstPage();

    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      this.boundOnSearchIconClick = this.onSearchInputChange.bind(this);
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  ngOnDestroy(): void {
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  onSearchCancel(): void {
    this.query = "";
    this.currentPage = 0;
    this.findUsers();
  }

  onSearchInputChange(): void {
    this.currentPage = 0;
    this.findUsers();
  }

  firstPage() {
    this.currentPage = 0;
    this.findUsers();
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.findUsers();
    }
  }

  setPage(page) {
    if (page >= 0 && page <= this.totalPageCount && page !== this.currentPage) {
      this.currentPage = page;
      this.findUsers();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPageCount - 1) {
      this.currentPage++;
      this.findUsers();
    }
  }

  lastPage() {
    if (this.currentPage !== this.totalPageCount - 1) {
      this.currentPage = this.totalPageCount - 1;
      this.findUsers();
    }
  }

  addControlsForUsers() {
    this.users.forEach((item: UserType) => {
      const percentageInput = this.formBuilder.control({
        value: item.percentage,
        disabled: false,
      });
      this.usersForm.addControl(item.pk + item.sk, percentageInput);
    });
  }

  findUsers(): void {
    const sortOrder = this.sortOptions.ascending ? "asc" : "desc";
    const sortBy = this.sortOptions.column;
    const page = this.currentPage + 1;
    this.utilityService.increaseSpinnerCount("Loading users...");
    this.isLoading = true;
    this.hasValidInput = false;
    this.qaQueueService
      .searchQAQueueActions(
        this.tenantId,
        "USER",
        this.query,
        sortBy,
        sortOrder,
        this.pageSize,
        page
      )
      .subscribe(
        (data) => {
          this.usersPayload = data.data as UserType[];
          this.users = JSON.parse(JSON.stringify(this.usersPayload));
          this.pageSize = data.pageSize;
          this.totalPageCount = Math.ceil(data.total / this.pageSize);
          this.addControlsForUsers();
          this.isLoading = false;
        },
        (error) => {
          console.error(error);
          this.displayToaster(
            "An error occurred while loading visit  types",
            "error"
          );
        },
        () => {
          this.utilityService.decreaseSpinnerCount();
          this.isLoading = false;
        }
      );
  }

  sortBy(selectedCol: string) {
    this.currentPage = 0;
    // Toggle direction ONLY when re-clicking the column already sorted on; switching
    // to a DIFFERENT column starts at ascending. Toggling unconditionally (the
    // previous behaviour) made a new column's first direction depend on whichever
    // column happened to be sorted before it — so the same click produced asc or desc
    // arbitrarily, and the arrow icon disagreed with what the user expected. This
    // mirrors location-teams.ts, the established pattern for this app's grids.
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.ascending = !this.sortOptions.ascending;
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.ascending = true;
    }
    this.findUsers();
  }

  displayToaster(message: string, cssClass: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 10000,
      position: "below",
      cssClass: cssClass,
    });
    toast.present();
  }

  onInput(event: any, controlName: string) {
    const input = event.target;
    let value = input.value;

    // Remove leading zeros
    if (value.length > 0) {
      value = Number(value);
    }
    this.usersForm.controls[controlName].setValue(value);
    this.checkIfFormIsChanged();
    if (value === "") this.hasValidInput = false;
  }

  getChangedUsers() {
    const usersToSubmit: UserType[] = [];
    for (let i = 0; i < this.usersPayload.length; i++) {
      if (+this.usersPayload[i].percentage !== +this.users[i].percentage) {
        usersToSubmit.push(this.users[i]);
      }
    }
    return usersToSubmit;
  }

  checkIfFormIsChanged() {
    let changed = false;
    for (let i = 0; i < this.usersPayload.length; i++) {
      if (+this.usersPayload[i].percentage !== +this.users[i].percentage) {
        changed = true;
        break;
      }
    }
    this.hasValidInput = changed;
  }

  onSubmit(): void {
    // Defence in depth: the disabled button is only a hint, so refuse here too.
    if (!this.canEdit) return;
    this.isLoading = true;
    this.utilityService.increaseSpinnerCount("Submitting...");
    this.qaQueueService
      .putUsers(this.tenantId, { users: this.getChangedUsers() })
      .subscribe(
        (_) => {
          this.utilityService.decreaseSpinnerCount();
          this.displayToaster("Submitted successfully", "success");
          this.hasValidInput = false;
          this.updateUserListWithChangesSubmitted(this.getChangedUsers());
        },
        (err) => {
          console.error(err);
          this.displayToaster(
            "An error occurred while updating user(s)",
            "error"
          );
          this.isLoading = false;
          this.utilityService.decreaseSpinnerCount();
        }
      );
  }

  restrictNonNumeric(event: any) {
    const input = event.target;
    const pattern = /^[0-9]*$/;

    if (!pattern.test(event.key)) {
      event.preventDefault();
    } else {
      const value = input.value + event.key;

      if (Number(value) > 100) {
        event.preventDefault();
      }
    }
  }

  updateUserListWithChangesSubmitted(usersToSubmit: UserType[]) {
    for (let i = 0; i < usersToSubmit.length; i++) {
      const index = this.usersPayload.findIndex(
        (user) =>
          user.pk === usersToSubmit[i].pk && user.sk === usersToSubmit[i].sk
      );
      if (index !== -1) {
        this.usersPayload[index] = { ...this.users[index] };
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/qa-control/users-grid/users-grid.ts